/* @flow */
import * as React from 'react';

import clsx from 'clsx';
import shallowequal from 'shallowequal';

import type {
  InstancePrice,
  ProductFeature as InstancePriceFeature,
} from '@gandi/redux-resource-price';
import { instances } from '@gandi/redux-resource-simplehosting';

import { Price } from '@gandi/translate';

import { Form, RadioButtonGroupField } from 'gandi.react-form';

import type { PresenterProps } from './Presenter';

import styles from './SimpleHostingCreate.scss';

type ButonGroupItem = {
  label: React.Node,
  value: string,
  footer?: React.Node,
  ...
};

// We'd like to get rid of features that are of type "boolean"
// (they have no value associated) and are not provided/available.
const shouldIncludeFeature = (feature: InstancePriceFeature): boolean => {
  // TODO We use a transition api right now, and "boolean" are in fact "strings"
  // with 'yes'/'no' values.
  // When the https://phabricator.corp.gandi.net/T13687 is resolved, then
  // we can remove it.
  if (feature.type === 'string' && feature.value === 'no') return false;
  if (feature.type === 'boolean' && feature.value === 'false') return false;
  return true;
};
// We'd like to display feature's `name` only when the feature is of type
// "boolean".
const shouldDisplayFeatureValue = (feature: InstancePriceFeature): boolean => {
  // TODO We use a transition api right now, and "boolean" are in fact "strings"
  // with 'yes'/'no' values.
  // When the https://phabricator.corp.gandi.net/T13687 is resolved, then
  // we can remove it.
  if (feature.type === 'string' && feature.value === 'yes') return false;
  if (feature.type === 'boolean' && feature.value === 'true') return false;
  return true;
};

class InstanceCreateForm extends React.Component<PresenterProps> {
  durationUnitLabel: { d: any, m: any, y: any } = {
    d: this.props.translator.__('day'),
    m: this.props.translator.__('month'),
    y: this.props.translator.__('year'),
  };

  sizeTarget: { l: any, m: any, 's+': any, xl: any, xxl: any } = {
    [instances.INSTANCE_SIZE_S_PLUS]: this.props.translator.__(
      'For small websites with small traffic',
    ),
    [instances.INSTANCE_SIZE_M]: this.props.translator.__(
      'CMS, blogs, and apps with little traffic',
    ),
    [instances.INSTANCE_SIZE_L]: this.props.translator.__(
      'E-commerce, websites with mild traffic',
    ),
    [instances.INSTANCE_SIZE_XL]: this.props.translator.__(
      'E-commerce, websites with moderate traffic',
    ),
    [instances.INSTANCE_SIZE_XXL]: this.props.translator.__(
      'E-commerce, CMS, blogs, and applications with intensive traffic',
    ),
  };

  notInSharedFeatures: (feature: InstancePrice['features'][0]) => boolean = (
    feature: InstancePrice['features'][0],
  ) =>
    !this.props.instancePrices.sharedFeatures.find(item =>
      shallowequal(item, feature),
    );

  formatPrice: (
    price: number,
    durationUnit: InstancePrice['durationUnit'],
    normalPrice?: number,
  ) => React.MixedElement = (
    price: number,
    durationUnit: InstancePrice['durationUnit'],
    normalPrice?: number,
  ) => {
    const { __ } = this.props.translator;
    const currency = this.props.currency;
    const unitLabel = this.durationUnitLabel[durationUnit];

    if (Number.isNaN(price) || !currency) {
      return <span>{__('Price unavailable')}</span>;
    }

    if (price === 0) {
      return (
        <span className={styles.FormatPrice__rebatePrice}>{__('Free')}</span>
      );
    }

    if (!normalPrice) {
      return (
        <span>
          <Price currency={currency}>{price}</Price>/{unitLabel}
        </span>
      );
    }

    return (
      <span>
        <span className={styles.FormatPrice__fullPrice}>
          <Price currency={currency}>{normalPrice || price}</Price>
        </span>
        <span>&nbsp;</span>
        <span className={styles.FormatPrice__rebatePrice}>
          <Price currency={currency}>{price}</Price>/{unitLabel}
        </span>
      </span>
    );
  };

  renderTrialSizeItem: (price: InstancePrice) => ButonGroupItem = (
    price: InstancePrice,
  ) => {
    const { __ } = this.props.translator;
    const formatPrice = this.formatPrice;
    const size = price.options.size;
    const label = price.options.sizeLabel;
    const duration = price.maxDuration;
    const { taxesIncluded } = this.props;

    const item: ButonGroupItem = {
      label: [
        __('Trial %(duration)s days', { duration }),
        <br key={`br-${size}`} />,
        <div className={styles.FormatPrice__root} key={`div-${size}`}>
          {formatPrice(
            taxesIncluded ? price.priceAfterTaxes : price.priceBeforeTaxes,
            price.durationUnit,
            taxesIncluded
              ? price.normalPriceAfterTaxes
              : price.normalPriceBeforeTaxes,
          )}
        </div>,
      ],
      value: 'trial',
    };

    item.footer = (
      <ul className={styles.features}>
        <li>
          {__('Same features as pack "%(packName)s"', { packName: label })}
        </li>
        <li>{__('Resize for free during trial')}</li>
        <li>{__('No strings attached')}</li>
      </ul>
    );

    return item;
  };

  renderSizeItem: (price: InstancePrice) => ButonGroupItem = (
    price: InstancePrice,
  ) => {
    const { taxesIncluded } = this.props;

    const formatPrice = this.formatPrice;
    const size = price.options.size;
    const label = price.options.sizeLabel;
    // $FlowFixMe[invalid-computed-prop]
    const target = this.sizeTarget[size];

    const item: ButonGroupItem = {
      label: [
        label,
        <br key={`br-${size}`} />,
        <div className={styles.FormatPrice__root} key={`div-${size}`}>
          {formatPrice(
            taxesIncluded ? price.priceAfterTaxes : price.priceBeforeTaxes,
            price.durationUnit,
            taxesIncluded
              ? price.normalPriceAfterTaxes
              : price.normalPriceBeforeTaxes,
          )}
        </div>,
      ],
      value: size,
    };

    item.footer = (
      <div className={styles.features}>
        {!!target && <p>{target}</p>}
        <ul>
          {price.features
            .filter(this.notInSharedFeatures)
            .filter(shouldIncludeFeature)
            // eslint-disable-next-line prefer-arrow-callback
            .map(function renderFeature(feature, idx) {
              /* eslint-disable react/no-array-index-key */
              // This is "safe" to use index as key, features are statics,
              // and therefore will never be reindexed
              return (
                <li key={`offer-feature-${idx}`}>
                  {shouldDisplayFeatureValue(feature)
                    ? `${
                        feature.value_label
                      } ${feature.name_label.toLowerCase()}`
                    : feature.name_label}
                </li>
              );
              /* eslint-enable react/no-array-index-key */
            })}
        </ul>
      </div>
    );

    return item;
  };

  getSizeItems(): Array<ButonGroupItem> {
    // $FlowFixMe[missing-type-arg]
    const sizeItems = this.props.instancePrices.all.map<ButonGroupItem>(
      this.renderSizeItem,
    );

    if (
      !('cross_sell' in this.props.location.query) &&
      this.props.instancePrices.trial
    ) {
      return [this.renderTrialSizeItem(this.props.instancePrices.trial)].concat(
        sizeItems,
      );
    }
    return sizeItems;
  }

  render(): React.Node {
    const { __ } = this.props.translator;

    return (
      <Form
        id={this.props.formName}
        onSubmit={this.props.form.handleSubmit}
        // $FlowFixMe[incompatible-type] TODO: Remove deprecated string ref
        ref={this.props.formName}
        className="form-stacked"
      >
        <RadioButtonGroupField
          full
          className={clsx(styles.radioButtonGroup, styles.sizeItems)}
          help={__(
            'Every instance comes with %(features)s, and unlimited bandwidth.',
            {
              features: this.props.instancePrices.sharedFeatures
                .map(feature =>
                  shouldDisplayFeatureValue(feature)
                    ? `${
                        feature.value_label
                      } ${feature.name_label.toLowerCase()}`
                    : feature.name_label,
                )
                .join(','),
            },
          )}
          items={this.getSizeItems()}
          label={__('Size')}
          name="size"
          required
        />
      </Form>
    );
  }
}

export default InstanceCreateForm;
